import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Async thunk: Fetch all bills from the backend
export const fetchBills = createAsyncThunk(
  'bills/fetchBills',
  async (_, thunkAPI) => {
    try {
      const res = await fetch('/api/v1/bills', {
        method: 'GET',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

// Async thunk: Preview an existing bill before completing payment
export const previewBill = createAsyncThunk(
  'bills/previewBill',
  async ({ patientId, category, serviceId, patientQueueId }, thunkAPI) => {
    try {
      const res = await fetch('/api/v1/bills/preview_existing_bills', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ bill: { patient_id: patientId, category,service_id: serviceId, patient_queue_id: patientQueueId } }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not preview bill');
      return data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

// Async thunk: Confirm a bill payment
export const confirmPayment = createAsyncThunk(
  'bills/confirmPayment',
  async ({ patientId, category, serviceId, patientQueueId, paymentMethod }, thunkAPI) => {
    try {
      const res = await fetch(`/api/v1/bills/confirm_payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          bill: { patient_id: patientId, category, service_id: serviceId, patient_queue_id: patientQueueId, payment_method: paymentMethod }
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Confirm payment failed');
      return data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

// Async thunk: Complete a bill payment (cash flow)
export const completePayment = createAsyncThunk(
  'bills/completePayment',
  async (billId, thunkAPI) => {
    try {
      const res = await fetch(`/api/v1/bills/${billId}/complete_payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Payment completion failed');
      return data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

// Async thunk: Initiate M-Pesa STK Push
export const initiateMpesaPayment = createAsyncThunk(
  'bills/initiateMpesaPayment',
  async ({ billId, phoneNumber }, thunkAPI) => {
    try {
      const res = await fetch(`/api/v1/mpesa/stk_push`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ bill_id: billId, phone_number: phoneNumber }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'M-Pesa initiation failed');
      return data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

// Async thunk: Check M-Pesa payment status
export const checkMpesaStatus = createAsyncThunk(
  'bills/checkMpesaStatus',
  async (checkoutRequestId, thunkAPI) => {
    try {
      const res = await fetch(`/api/v1/mpesa/status/${checkoutRequestId}`, {
        method: 'GET',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Status check failed');
      return data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.message);
    }
  }
);

// Initial state of bills slice
const initialState = {
  list: [],
  loading: false,
  loaded: false,
  error: null,
  selected: null,
  preview: null,
  showPaymentModal: false,
  mpesaStatus: null,
  mpesaLoading: false,
  checkoutRequestId: null,
};

const billSlice = createSlice({
  name: 'bills',
  initialState,
  reducers: {
    clearPreview(state) {
      state.preview = null;
      state.showPaymentModal = false;
      state.mpesaStatus = null;
      state.checkoutRequestId = null;
    },
  billAdded(state, action) {
    const newBill = action.payload;
    const exists = state.list.some((b) => b.id === newBill.id);
    if (!exists) {
      state.list.push(newBill);
    }
  },
 billUpdated(state, action) {
    const updated = action.payload;
    const idx = state.list.findIndex((b) => b.id === updated.id);
    if (idx !== -1) {
      state.list[idx] = updated;
    }
  },
    billRemoved(state, action) {
      state.list = state.list.filter((b) => b.id !== action.payload);
    },
    clearMpesaStatus(state) {
      state.mpesaStatus = null;
      state.checkoutRequestId = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchBills.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBills.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
        state.loaded = true;
      })
      .addCase(fetchBills.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch bills';
      })
      .addCase(previewBill.fulfilled, (state, action) => {
         const { serviceId, patientQueueId } = action.meta.arg;
          state.preview = {
            ...action.payload,
            service_id: serviceId,
            patient_queue_id: patientQueueId,
          };
          state.showPaymentModal = true;
      })
      .addCase(previewBill.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(completePayment.fulfilled, (state) => {
        state.preview = null;
        state.showPaymentModal = false;
        state.mpesaStatus = null;
        state.checkoutRequestId = null;
      })
      .addCase(completePayment.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(initiateMpesaPayment.pending, (state) => {
        state.mpesaLoading = true;
        state.mpesaStatus = 'sending';
      })
      .addCase(initiateMpesaPayment.fulfilled, (state, action) => {
        state.mpesaLoading = false;
        state.mpesaStatus = 'sent';
        state.checkoutRequestId = action.payload.checkout_request_id;
      })
      .addCase(initiateMpesaPayment.rejected, (state, action) => {
        state.mpesaLoading = false;
        state.mpesaStatus = 'error';
        state.error = action.payload;
      })
      .addCase(checkMpesaStatus.fulfilled, (state, action) => {
        state.mpesaStatus = action.payload.status;
      })
      .addCase(checkMpesaStatus.rejected, (state, action) => {
        state.mpesaStatus = 'error';
        state.error = action.payload;
      });
  },
});

// Selectors
export const { clearPreview, billAdded, billRemoved, billUpdated, clearMpesaStatus } = billSlice.actions;
export const selectBills = (state) => state.bills.list;
export const selectBillsLoading = (state) => state.bills.loading;
export const selectBillsError = (state) => state.bills.error;
export const selectBillPreview = (state) => state.bills.preview;
export const selectShowPaymentModal = (state) => state.bills.showPaymentModal;
export const selectMpesaStatus = (state) => state.bills.mpesaStatus;
export const selectMpesaLoading = (state) => state.bills.mpesaLoading;
export const selectCheckoutRequestId = (state) => state.bills.checkoutRequestId;

export default billSlice.reducer;
